import React, { useMemo, useState, useCallback } from 'react';
import classNames from 'classnames';
import { DocumentEditor, IConfig } from '@onlyoffice/document-editor-react';
import {
  DOCUMENT_TYPE,
  SERVER_URL,
  EDITOR_CONFIG,
  FILE_TYPE,
  PERMISSIONS,
} from './data';
import FileWatermark from 'components/FileWatermark';
import { FileWatermarkProps } from 'components/FileWatermark/FileWatermark.component';
import { FilePreviewWithIpAddress } from 'models/File';
import { uuid4 } from '@sentry/utils';

import './OfficeFilePreview.styles.scss';

type OfficeFilePreviewProps = {
  className?: string;
  token?: string;
  data: FilePreviewWithIpAddress;
  onDocumentLoad?: () => void;
} & IConfig &
  FileWatermarkProps;

const OfficeFilePreview: React.FC<OfficeFilePreviewProps> = (props) => {
  const {
    className,
    token,
    data,
    fullName,
    email,
    watermarkSettings,
    editorConfig = EDITOR_CONFIG,
    onDocumentLoad,
    ...rest
  } = props;

  const { url, type, ipAddress } = data || {};

  const [isLoaded, setIsLoaded] = useState(false);

  const classes = classNames('office-file-preview', className);

  const documentType = useMemo(() => DOCUMENT_TYPE[type] || type, [type]);

  const fileType = useMemo(() => FILE_TYPE[type] || type, [type]);

  const document = useMemo(
    () => ({
      fileType,
      url,
      title: url,
      key: uuid4(),
      permissions: PERMISSIONS,
    }),
    [fileType, url],
  );

  const editorConfigWithRestrictions = useMemo(
    () => ({
      ...editorConfig,
      user: {
        ...editorConfig?.user,
        ...(fullName && { name: fullName, id: fullName }),
      },
    }),
    [editorConfig, fullName],
  );

  const handleDocumentReady = useCallback(() => {
    setIsLoaded(true);
    onDocumentLoad?.();
  }, [onDocumentLoad]);

  if (!url || !SERVER_URL) return null;

  return (
    <div className={classes}>
      <DocumentEditor
        id="docxEditor"
        documentServerUrl={SERVER_URL}
        config={{
          ...rest,
          documentType,
          token,
          document,
          editorConfig: editorConfigWithRestrictions,
        }}
        events_onDocumentReady={handleDocumentReady}
      />

      {isLoaded && (
        <FileWatermark
          fullName={fullName}
          email={email}
          ipAddress={ipAddress}
          watermarkSettings={watermarkSettings}
        />
      )}
    </div>
  );
};

export default OfficeFilePreview;
